import React, { useState } from "react"
import {
	FaInfoCircle,
	FaUserFriends,
	FaCalendarDay,
	FaArrowDown,
	FaCheck,
	FaChevronDown,
	FaChevronUp,
	FaUsers,
	FaBullseye
} from "react-icons/fa"
import { isCenterClosed } from "../../../utils/generalUtils"

// Helper function to filter days when center is open
function filterOpenDays(futureHuddleData, centerOpen, centerClosedDates = []) {
	return futureHuddleData.filter(day => {
		const dateObj = new Date(day.date + "T12:00:00-08:00")
		const patients = day.metrics?.current_scheduled_patients || 0
		return !isCenterClosed(dateObj, centerOpen, centerClosedDates) && patients > 0
	})
}

function getSumScheduledPatients(futureHuddleData, centerOpen, centerClosedDates = []) {
	const openDays = filterOpenDays(futureHuddleData, centerOpen, centerClosedDates)
	return openDays.reduce((sum, day) => sum + (day.metrics?.current_scheduled_patients || 0), 0)
}

function getSumExpectedPatients(futureHuddleData, centerOpen, centerClosedDates = []) {
	const openDays = filterOpenDays(futureHuddleData, centerOpen, centerClosedDates)
	return openDays.reduce((sum, day) => sum + (day.metrics?.expected_patients || 0), 0)
}

function getSumTargetPatients(futureHuddleData, centerOpen, centerClosedDates = []) {
	const openDays = filterOpenDays(futureHuddleData, centerOpen, centerClosedDates)
	return openDays.reduce((sum, day) => sum + (day.metrics?.target_patients || 0), 0)
}

function getDayOfWeekShort(dateStr) {
	const date = new Date(dateStr + "T00:00:00")
	return date.toLocaleDateString(undefined, { weekday: "short" })
}

const PatientSummary = ({ futureHuddleData, settings }) => {
	const [showAllDays, setShowAllDays] = useState(false)
	const centerOpen = settings?.centerOpen
	const centerClosedDates = settings?.center_closed_dates || []

	const scheduled = getSumScheduledPatients(futureHuddleData, centerOpen, centerClosedDates)
	const expected = getSumExpectedPatients(futureHuddleData, centerOpen, centerClosedDates)
	const target = getSumTargetPatients(futureHuddleData, centerOpen, centerClosedDates)

	// Calculate percentage below target
	const percentBelowTarget = target > 0 ? Math.round(((target - expected) / target) * 100) : 0
	const percentAboveTarget = target > 0 ? Math.round(((expected - target) / target) * 100) : 0
	const isBelow = expected < target
	const isAboveOrEqual = expected >= target

	// Filter days with scheduled patients and only include open days
	const daysWithScheduled = filterOpenDays(
		futureHuddleData,
		centerOpen,
		centerClosedDates
	).filter(day => day.metrics?.current_scheduled_patients > 0)

	// Filter days with target patients and only include open days
	const daysWithTargets = filterOpenDays(futureHuddleData, centerOpen, centerClosedDates).filter(
		day => day.metrics?.target_patients > 0
	)

	// Determine which patients to show
	const scheduledToShow = showAllDays ? daysWithScheduled : daysWithScheduled.slice(0, 5)
	const targetsToShow = showAllDays ? daysWithTargets : daysWithTargets.slice(0, 5)

	return (
		<div className="bg-white rounded-lg shadow p-6 flex flex-col min-h-[220px]">
			<h2 className="text-2xl font-semibold mb-5 flex items-center">
				<div className="p-2 bg-blue-100 rounded-full mr-2">
					<FaUserFriends style={{ color: "#352D66" }} />
				</div>
				<span style={{ color: "#352D66" }}>Patient Summary</span>
			</h2>

			{/* Scheduled Patients Section */}
			<div className="bg-gradient-to-r from-blue-50 to-white rounded-lg p-3 shadow-sm border-l-4 border-blue-500 mb-4">
				<div className="flex items-center justify-between mb-2">
					<div className="flex items-center">
						<div className="p-1.5 bg-blue-100 rounded-full mr-2">
							<FaUsers className="text-blue-600 text-sm" />
						</div>
						<span className="font-semibold text-gray-800">Scheduled Patients</span>
					</div>
					<div className="text-2xl font-bold text-blue-600">{scheduled}</div>
				</div>

				{daysWithScheduled.length > 0 && (
					<div className="mt-3">
						<div className="flex flex-wrap gap-2 mt-1">
							{scheduledToShow.map(day => (
								<div
									key={day.date}
									className="flex flex-col items-center bg-white rounded-lg px-3 py-1.5 shadow-sm border border-blue-100 hover:shadow-md transition-all"
								>
									<span className="text-gray-500 text-xs">
										{getDayOfWeekShort(day.date)}
									</span>
									<span className="font-semibold text-blue-700">
										{day.metrics.current_scheduled_patients}
									</span>
								</div>
							))}
						</div>

						{daysWithScheduled.length > 5 && (
							<button
								onClick={() => setShowAllDays(!showAllDays)}
								className="mt-2 text-xs flex items-center text-blue-600 hover:text-blue-800 focus:outline-none"
							>
								{showAllDays ? (
									<>
										<span>Show Less</span>
										<FaChevronUp className="ml-1 text-xs" />
									</>
								) : (
									<>
										<span>Show All {daysWithScheduled.length} Days</span>
										<FaChevronDown className="ml-1 text-xs" />
									</>
								)}
							</button>
						)}
					</div>
				)}
			</div>

			{/* Expected Patients Section */}
			<div className="bg-gradient-to-r from-gray-50 to-white rounded-lg p-3 shadow-sm border-l-4 border-gray-400 mb-4">
				<div className="flex items-center justify-between">
					<div className="flex items-center">
						<div className="p-1.5 bg-gray-200 rounded-full mr-2">
							<FaCalendarDay className="text-gray-600 text-sm" />
						</div>
						<span className="font-semibold text-gray-800">Expected Patients</span>
					</div>
					<div className="flex items-center">
						<div className="text-xl font-semibold text-gray-700 mr-2">{expected}</div>
						{isBelow && (
							<div className="bg-red-100 text-red-700 text-xs px-2 py-1 rounded-full flex items-center">
								<FaArrowDown className="mr-1" />
								{percentBelowTarget}% below target
							</div>
						)}
						{isAboveOrEqual && target > 0 && (
							<div className="bg-green-100 text-green-700 text-xs px-2 py-1 rounded-full flex items-center">
								<FaCheck className="mr-1" />
								{expected === target
									? "At target"
									: `${percentAboveTarget}% above target`}
							</div>
						)}
					</div>
				</div>
				<div className="flex items-center ml-8 mt-1 text-xs text-gray-500">
					<FaInfoCircle className="mr-1 text-gray-400" />
					Adjusted for historical no-shows and add-ons
				</div>
			</div>

			{/* Target Patients Section */}
			<div className="bg-gradient-to-r from-green-50 to-white rounded-lg p-3 shadow-sm border-l-4 border-green-500">
				<div className="flex items-center justify-between mb-2">
					<div className="flex items-center">
						<div className="p-1.5 bg-green-100 rounded-full mr-2">
							<FaBullseye className="text-green-600 text-sm" />
						</div>
						<span className="font-semibold text-gray-800">Target Patients</span>
					</div>
					<div className="flex items-center">
						<div className="bg-green-100 text-green-800 px-2 py-0.5 rounded-full text-sm font-medium">
							Total: {target}
						</div>
					</div>
				</div>

				{daysWithTargets.length > 0 && (
					<div className="flex flex-wrap gap-2 mt-2 ml-8">
						{targetsToShow.map(day => (
							<div
								key={day.date}
								className="flex flex-col items-center bg-white rounded-lg px-3 py-1.5 shadow-sm border border-green-100 hover:shadow-md transition-all"
							>
								<span className="text-gray-500 text-xs">
									{getDayOfWeekShort(day.date)}
								</span>
								<span className="font-semibold text-green-700">
									{day.metrics.target_patients}
								</span>
							</div>
						))}
					</div>
				)}
			</div>
		</div>
	)
}

export default PatientSummary
